/**
 * Grouping manifest entries into the navigation tree.
 *
 * Split out of `docsNav.js` so it can be tested. `docsNav.js` calls
 * `import.meta.glob` at module scope, which only Vite understands, so the
 * whole module is unimportable from a `node --test` file. The interesting
 * behaviour — which category a page lands in, which space, what order
 * things come out in — is pure, and putting it here is what lets it be
 * covered without a browser or a bundler.
 */
import {
    DOC_SPACES,
    spaceForAudience,
    splitCategoryLabel
} from './docsCategory.js'

/**
 * Category order within a space.
 *
 * Anything unlisted sorts after these, alphabetically, so adding a
 * category never requires an edit here. It lands at the end until
 * somebody decides it belongs earlier.
 */
export const CATEGORY_ORDER = [
    // Using Power
    '📊 Your dashboards',
    '🌍 Reducing usage',
    '💷 Costs & budgets',
    '🔌 Meter data & alerts',
    '👥 Running your organisation',
    // Partner console
    '🤝 Partner console',
    // Operations & engineering
    '🔋 Product overview',
    '🚀 Onboarding customers',
    '🏢 Orgs & partners',
    '🛠️ Running the platform',
    '📈 Meter data pipelines',
    '🔐 Platform architecture',
    '🧩 Backend services',
    '⚡ Energy engines',
    '🗺️ Roadmap & research',
    '🛟 Help & support'
]

function categoryRank(label) {
    const i = CATEGORY_ORDER.indexOf(label)
    return i === -1 ? CATEGORY_ORDER.length : i
}

/**
 * Group manifest entries (each carrying the `audience` it came from) into
 * ordered categories.
 *
 * A category exists exactly when at least one of these pages declares it.
 * There is no registry of category names, so an empty category cannot be
 * produced, and dropping the last page in one removes it with no second
 * edit anywhere.
 */
export function groupNavPages(pages) {
    const byKey = new Map()

    for (const page of pages) {
        if (!page.category || !page.categorySlug) continue

        const space = spaceForAudience(page.audience)
        // Space is part of the key so a label reused across two spaces
        // stays two categories. The build script refuses that today;
        // keying on it means nothing has to notice if the rule changes.
        const key = `${space}:${page.categorySlug}`

        let category = byKey.get(key)
        if (!category) {
            const { emoji, text } = splitCategoryLabel(page.category)
            category = {
                label: page.category,
                emoji,
                text,
                slug: page.categorySlug,
                space,
                pages: []
            }
            byKey.set(key, category)
        }
        category.pages.push(page)
    }

    const categories = [...byKey.values()]
    for (const category of categories) {
        category.pages.sort((a, b) => a.title.localeCompare(b.title))
    }
    categories.sort(
        (a, b) =>
            DOC_SPACES.indexOf(a.space) - DOC_SPACES.indexOf(b.space) ||
            categoryRank(a.label) - categoryRank(b.label) ||
            a.label.localeCompare(b.label)
    )

    return categories
}

/** The categories of one space, in order. */
export function categoriesInSpace(nav, space) {
    return nav ? nav.categories.filter((c) => c.space === space) : []
}

/** Spaces that have at least one category for this reader, in order. */
export function spacesWithContent(nav) {
    return DOC_SPACES.filter(
        (space) => categoriesInSpace(nav, space).length > 0
    )
}

/** The category holding a given docs path, or undefined. */
export function categoryForPath(nav, path) {
    if (!nav) return undefined
    return nav.categories.find((c) => c.pages.some((p) => p.path === path))
}

/**
 * The category with this slug, or undefined.
 *
 * Looked up across every space rather than within one, because there is a
 * single `/docs/category/:slug` route. The build script refuses a slug
 * claimed by two spaces, which is what makes one route unambiguous.
 */
export function categoryBySlug(nav, slug) {
    if (!nav) return undefined
    return nav.categories.find((c) => c.slug === slug)
}
